/**
 * A barra lateral do chat: secções, contadores e como os eventos a atualizam sem voltar a pedir a lista (ADR-0017).
 * Funções puras, sem React.
 */

import type { Schemas } from "@/types/api";

export type ChatOverview = Schemas["ChatOverview"];
export type ConversationSummary = Schemas["ChatConversationSummary"];

export interface SidebarSections {
  /** As que a pessoa arquivou: fora da barra, atrás de «Arquivadas». */
  archived: ConversationSummary[];
  /** As que a pessoa fixou, de qualquer tipo, na ordem do servidor (a fixada há menos tempo em cima). */
  pinned: ConversationSummary[];
  channels: ConversationSummary[];
  direct: ConversationSummary[];
  bot: ConversationSummary[];
}

export function sidebarSections(conversations: ConversationSummary[]): SidebarSections {
  const sections: SidebarSections = { archived: [], pinned: [], channels: [], direct: [], bot: [] };
  for (const c of conversations) {
    if (c.archived_by_me) sections.archived.push(c);
    else if (c.pinned) sections.pinned.push(c);
    else if (c.is_bot_direct) sections.bot.push(c);
    else if (c.kind === "canal_publico" || c.kind === "canal_privado") sections.channels.push(c);
    else sections.direct.push(c);
  }
  sections.channels.sort((a, b) => a.title.localeCompare(b.title, "pt"));
  return sections;
}

export type RecencyBucket = "hoje" | "ontem" | "semana" | "mes" | "antigas";

const RECENCY_LABELS: Record<RecencyBucket, string> = {
  hoje: "Hoje",
  ontem: "Ontem",
  semana: "Últimos 7 dias",
  mes: "Últimos 30 dias",
  antigas: "Mais antigas",
};

const DAY_MS = 24 * 60 * 60 * 1000;

/** O dia na hora de quem lê (meia-noite local), para «hoje» e «ontem» não dependerem do fuso do servidor. */
function startOfDay(date: Date): number {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}

export function recencyBucket(lastActivity: string | null, now: Date = new Date()): RecencyBucket {
  // Sem mensagens (conversa acabada de abrir) conta como de hoje: é a mais recente de todas.
  if (!lastActivity) return "hoje";
  const days = Math.floor((startOfDay(now) - startOfDay(new Date(lastActivity))) / DAY_MS);
  if (days <= 0) return "hoje";
  if (days === 1) return "ontem";
  if (days < 7) return "semana";
  if (days < 30) return "mes";
  return "antigas";
}

/** Agrupa por antiguidade, mantendo a ordem recebida (o servidor já ordena por atividade). Só devolve os grupos com conversas. */
export function groupByRecency(conversations: ConversationSummary[], now: Date = new Date()): Array<{ bucket: RecencyBucket; label: string; items: ConversationSummary[] }> {
  const order: RecencyBucket[] = ["hoje", "ontem", "semana", "mes", "antigas"];
  const map = new Map<RecencyBucket, ConversationSummary[]>();
  for (const c of conversations) {
    const bucket = recencyBucket(c.last_message_at ?? null, now);
    map.set(bucket, [...(map.get(bucket) ?? []), c]);
  }
  return order.filter((b) => map.has(b)).map((bucket) => ({ bucket, label: RECENCY_LABELS[bucket], items: map.get(bucket) ?? [] }));
}

/**
 * Fixar ou desafixar uma conversa na cache (a resposta ao toque, antes do servidor). Reproduz a ordem do servidor: as
 * fixadas primeiro, a fixada há menos tempo em cima; as outras ficam como estavam. Mesma referência se nada muda.
 */
export function applyPin(overview: ChatOverview, conversationId: string, pinned: boolean, nowIso: string): ChatOverview {
  const target = overview.conversations.find((c) => c.id === conversationId);
  if (!target || target.pinned === pinned) return overview;
  const updated = overview.conversations.map((c) => (c.id === conversationId ? { ...c, pinned, pinned_at: pinned ? nowIso : null } : c));
  const pinnedFirst = updated.filter((c) => c.pinned).sort((a, b) => (b.pinned_at ?? "").localeCompare(a.pinned_at ?? ""));
  return { ...overview, conversations: [...pinnedFirst, ...updated.filter((c) => !c.pinned)] };
}

/**
 * Arquivar ou desarquivar uma conversa na cache (a resposta ao toque, antes do servidor). Arquivar tira também a fixação, como
 * no servidor. Mesma referência se nada muda.
 */
export function applyArchive(overview: ChatOverview, conversationId: string, archived: boolean, nowIso: string): ChatOverview {
  const target = overview.conversations.find((c) => c.id === conversationId);
  if (!target || target.archived_by_me === archived) return overview;
  return {
    ...overview,
    conversations: overview.conversations.map((c) =>
      c.id === conversationId ? { ...c, archived_by_me: archived, archived_by_me_at: archived ? nowIso : null, ...(archived ? { pinned: false, pinned_at: null } : {}) } : c,
    ),
  };
}

/** Total de não lidas, para o título do separador e o contador do menu. Conversas silenciadas não contam. */
export function totalUnread(conversations: ConversationSummary[]): number {
  // As arquivadas não contam: estão fora da barra e a pessoa pediu para as deixar de ver.
  return conversations.reduce((sum, c) => sum + (c.notify === "silenciar" || c.archived_by_me ? 0 : c.unread_count), 0);
}

/**
 * Uma mensagem nova numa conversa: sobe o cursor e, se não é minha nem estou a lê-la agora, conta como não lida.
 * Devolve a mesma referência quando nada muda (o React não volta a desenhar).
 */
export function applyNewMessage(
  overview: ChatOverview,
  args: { conversationId: string; seq: number; createdAt: string; mine: boolean; viewing: boolean },
): ChatOverview {
  let changed = false;
  const conversations = overview.conversations.map((c) => {
    if (c.id !== args.conversationId || args.seq <= c.last_seq) return c;
    changed = true;
    const read = args.mine || args.viewing ? args.seq : c.last_read_seq;
    return { ...c, last_seq: args.seq, last_read_seq: read, unread_count: Math.max(0, args.seq - read), last_message_at: args.createdAt };
  });
  if (!changed) return overview;
  conversations.sort((a, b) => (b.last_message_at ?? "").localeCompare(a.last_message_at ?? ""));
  return { ...overview, conversations };
}

export function applyRead(overview: ChatOverview, conversationId: string, seq: number): ChatOverview {
  let changed = false;
  const conversations = overview.conversations.map((c) => {
    if (c.id !== conversationId || seq <= c.last_read_seq) return c;
    changed = true;
    return { ...c, last_read_seq: seq, unread_count: Math.max(0, c.last_seq - seq) };
  });
  return changed ? { ...overview, conversations } : overview;
}

export function removeConversation(overview: ChatOverview, conversationId: string): ChatOverview {
  if (!overview.conversations.some((c) => c.id === conversationId)) return overview;
  return { ...overview, conversations: overview.conversations.filter((c) => c.id !== conversationId) };
}

export function tabTitle(base: string, unread: number): string {
  return unread > 0 ? `(${unread > 99 ? "99+" : unread}) ${base}` : base;
}
